import { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../hooks'
import { fetchReports, deleteReport } from '../slices/reportsSlice'
import SidebarNavigation from '../components/SidebarNavigation'
import './ReportsPage.css'

function StatusBadge({ status }: { status: string }) {
  const cls = `reports-status-badge reports-status-${status}`
  const label = status === 'completed' ? 'Completed'
    : status === 'generating' ? 'Generating...'
    : status === 'pending' ? 'Pending'
    : status === 'error' ? 'Error'
    : status
  return <span className={cls}>{label}</span>
}

function ReportsPage() {
  const dispatch = useAppDispatch()
  const { items: reports, loading, error } = useAppSelector((state) => state.reports)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    dispatch(fetchReports())
  }, [dispatch])

  // Poll while any report is pending/generating
  useEffect(() => {
    const hasActive = reports.some(r => r.status === 'pending' || r.status === 'generating')
    if (hasActive && !pollRef.current) {
      pollRef.current = setInterval(() => dispatch(fetchReports()), 5000)
    } else if (!hasActive && pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null } }
  }, [reports, dispatch])

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this report?')) return
    await dispatch(deleteReport(id))
  }

  return (
    <div data-testid="reports-page" className="reports-page">
      <SidebarNavigation />
      <main className="app-page-main">
        <header className="app-page-sticky-header">
          <div className="app-page-sticky-header-inner">
            <h1 className="app-page-title">Reports</h1>
          </div>
        </header>

        {loading && <div className="reports-loading">Loading reports...</div>}
        {error && <div className="reports-error">{error}</div>}

        {!loading && !error && reports.length === 0 && (
          <div className="reports-empty">No reports generated yet. Generate a report from the Summary section on the Test Failures page.</div>
        )}

        {!loading && reports.length > 0 && (
          <div className="reports-list">
            <table className="reports-table">
              <thead>
                <tr>
                  <th className="reports-table-th reports-col-title">Title</th>
                  <th className="reports-table-th reports-col-status">Status</th>
                  <th className="reports-table-th reports-col-filters">Filters</th>
                  <th className="reports-table-th reports-col-created">Created</th>
                  <th className="reports-table-th reports-col-actions"></th>
                </tr>
              </thead>
              <tbody>
                {reports.map((report) => (
                  <tr key={report.id} className="reports-table-row">
                    <td className="reports-table-td">
                      <Link to={`/reports/${report.id}`} className="reports-link">{report.title}</Link>
                    </td>
                    <td className="reports-table-td">
                      <StatusBadge status={report.status || 'completed'} />
                    </td>
                    <td className="reports-table-td">
                      <div className="reports-table-filters">
                        {report.tag_filter && <span className="reports-filter-badge">tag: {report.tag_filter}</span>}
                        {report.search_filter && <span className="reports-filter-badge">search: {report.search_filter}</span>}
                        {!report.tag_filter && !report.search_filter && <span className="reports-filter-none">All failures</span>}
                      </div>
                    </td>
                    <td className="reports-table-td reports-table-date">
                      {new Date(report.created_at).toLocaleDateString()} {new Date(report.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td className="reports-table-td">
                      <button type="button" className="reports-delete-btn" onClick={() => handleDelete(report.id)} title="Delete report">
                        ×
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  )
}

export default ReportsPage
